/**
 * /sign-up — email + password + optional first/last name.
 *
 * Flow (see references/clerk-flows.md §Sign-up):
 *   1. `signUp.create({ emailAddress, password, firstName?, lastName? })`
 *   2. `signUp.prepareEmailAddressVerification({ strategy: 'email_code' })`
 *   3. navigate to `/verify-email` — `useSignUp()` carries the state
 *      across the navigation because Clerk's client stores it on the
 *      `Client` resource.
 */

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Field, FieldError, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { signUpWithPassword } from '@/lib/auth';
import { useSignUp } from '@clerk/react/legacy';
import { Eye, EyeSlash } from '@phosphor-icons/react';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { type FormEvent, type ReactElement, useId, useState } from 'react';

export const Route = createFileRoute('/_auth/sign-up')({
  component: SignUpPage,
});

function SignUpPage(): ReactElement {
  const { signUp, isLoaded } = useSignUp();
  const navigate = useNavigate();
  const consentId = useId();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [consent, setConsent] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string | undefined>>({});
  const [formError, setFormError] = useState<string | undefined>(undefined);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!isLoaded || !signUp) return;

    if (!consent) {
      setFieldErrors({ consent: 'Please accept the Terms and Privacy Policy to continue.' });
      return;
    }

    setSubmitting(true);
    setFieldErrors({});
    setFormError(undefined);

    const result = await signUpWithPassword({
      signUp,
      emailAddress: email,
      password,
      firstName: firstName || undefined,
      lastName: lastName || undefined,
    });

    if (result.kind === 'error') {
      setFieldErrors(result.fieldErrors);
      setFormError(result.formError);
      setSubmitting(false);
      return;
    }

    if (result.kind === 'needs_verification') {
      navigate({ to: '/verify-email' });
      return;
    }

    // Edge: Clerk returned `complete` without verification (unusual
    // for email+password but possible if the instance disables it).
    navigate({ to: '/onboarding' });
  }

  return (
    <div className="space-y-6">
      <header className="space-y-1 text-center">
        <h1 className="text-xl font-semibold text-[var(--color-ink)]">Create your account</h1>
        <p className="text-sm text-[var(--color-mute)]">
          Sign up for ClickWeave. We'll email you a code to verify your address.
        </p>
      </header>

      <form className="space-y-4" onSubmit={onSubmit} noValidate>
        <div className="grid grid-cols-2 gap-3">
          <Field>
            <FieldLabel htmlFor="first-name">First name</FieldLabel>
            <Input
              id="first-name"
              autoComplete="given-name"
              value={firstName}
              onChange={(event) => setFirstName((event.target as HTMLInputElement).value)}
              aria-invalid={fieldErrors.firstName ? true : undefined}
            />
            {fieldErrors.firstName && <FieldError>{fieldErrors.firstName}</FieldError>}
          </Field>
          <Field>
            <FieldLabel htmlFor="last-name">Last name</FieldLabel>
            <Input
              id="last-name"
              autoComplete="family-name"
              value={lastName}
              onChange={(event) => setLastName((event.target as HTMLInputElement).value)}
              aria-invalid={fieldErrors.lastName ? true : undefined}
            />
            {fieldErrors.lastName && <FieldError>{fieldErrors.lastName}</FieldError>}
          </Field>
        </div>

        <Field>
          <FieldLabel htmlFor="email">Email</FieldLabel>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(event) => setEmail((event.target as HTMLInputElement).value)}
            aria-invalid={fieldErrors.emailAddress ? true : undefined}
          />
          {fieldErrors.emailAddress && <FieldError>{fieldErrors.emailAddress}</FieldError>}
        </Field>

        <Field>
          <FieldLabel htmlFor="password">Password</FieldLabel>
          <div className="relative w-full">
            <Input
              id="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              required
              minLength={8}
              value={password}
              onChange={(event) => setPassword((event.target as HTMLInputElement).value)}
              aria-invalid={fieldErrors.password ? true : undefined}
            />
            <button
              type="button"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              onClick={() => setShowPassword((prev) => !prev)}
              className="absolute inset-y-0 right-2 flex items-center text-[var(--color-mute)] hover:text-[var(--color-ink)]"
            >
              {showPassword ? (
                <EyeSlash aria-hidden="true" size={16} />
              ) : (
                <Eye aria-hidden="true" size={16} />
              )}
            </button>
          </div>
          {fieldErrors.password ? (
            <FieldError>{fieldErrors.password}</FieldError>
          ) : (
            <p className="text-xs text-[var(--color-mute)]">At least 8 characters.</p>
          )}
        </Field>

        {formError && (
          <div
            className="rounded-md border border-[var(--color-danger)]/30 bg-[var(--color-danger)]/10 px-3 py-2 text-sm text-[var(--color-danger)]"
            role="alert"
          >
            {formError}
          </div>
        )}

        <div className="flex items-start gap-2">
          <Checkbox
            id={consentId}
            checked={consent}
            onCheckedChange={(checked) => setConsent(checked === true)}
            aria-invalid={fieldErrors.consent ? true : undefined}
            className="mt-0.5"
          />
          <label htmlFor={consentId} className="text-xs text-[var(--color-mute)]">
            I agree to the{' '}
            <a
              href="https://www.clickweave.co/terms"
              target="_blank"
              rel="noreferrer"
              className="font-medium text-[var(--color-accent-brand)] hover:underline"
            >
              Terms of Service
            </a>{' '}
            and{' '}
            <a
              href="https://www.clickweave.co/privacy"
              target="_blank"
              rel="noreferrer"
              className="font-medium text-[var(--color-accent-brand)] hover:underline"
            >
              Privacy Policy
            </a>
            .
          </label>
        </div>
        {fieldErrors.consent && (
          <p className="-mt-2 text-xs text-[var(--color-danger)]" role="alert">
            {fieldErrors.consent}
          </p>
        )}

        {/* Clerk requires a CAPTCHA element to be present on the page
            for bot protection. Hidden unless Clerk elects to use it. */}
        <div id="clerk-captcha" />

        <Button type="submit" className="w-full" disabled={submitting || !consent}>
          {submitting ? 'Creating account…' : 'Create account'}
        </Button>
      </form>

      <p className="text-center text-sm text-[var(--color-mute)]">
        Already have an account?{' '}
        <Link
          to="/sign-in"
          className="font-medium text-[var(--color-accent-brand)] hover:underline"
        >
          Sign in
        </Link>
      </p>
    </div>
  );
}
